import { Builder } from "flatbuffers";
import {
  BallHitDataWireT,
  BallHitLaunchWireT,
  BallHitRefinedWireT,
  BallPolynomialDataWire,
  BallPolynomialWire,
  BallPolynomialWireT,
  BallPitchDataWireT,
  BallPitchRefinedWireT,
  Vec3WireT,
  BallBounceDataWireT,
  BallPitchDataReleaseDataWireT,
  BallPitchDataTrajectoryDataWireT,
  BallThrowDataT
} from "../../flatbuffers/mannequin";
import { reverseRecord } from "../../util";

export enum BallPolynomialType {
  BOUNCE_DATA = "bounce_data",
  HIT_DATA = "hit_data",
  HIT_LAUNCH = "hit_launch",
  HIT_REFINED = "hit_refined",
  PITCH_DATA = "pitch_data",
  // TODO: work out if these actually exist
  // PITCH_LOCATION = "pitch_location",
  // PITCH_RELEASE = "pitch_release",
  PITCH_REFINED = "pitch_refined",
  THROW_DATA = "throw_data",
}

const ballPacketToWire: Record<BallPolynomialType, BallPolynomialDataWire> = {
  [BallPolynomialType.BOUNCE_DATA]: BallPolynomialDataWire.BallBounceDataWire,
  [BallPolynomialType.HIT_DATA]: BallPolynomialDataWire.BallHitDataWire,
  [BallPolynomialType.HIT_LAUNCH]: BallPolynomialDataWire.BallHitLaunchWire,
  [BallPolynomialType.HIT_REFINED]: BallPolynomialDataWire.BallHitRefinedWire,
  [BallPolynomialType.PITCH_DATA]: BallPolynomialDataWire.BallPitchDataWire,
  // [BallPolynomialType.PITCH_LOCATION]: BallPolynomialDataWire.BallPitchLocationWire,
  // [BallPolynomialType.PITCH_RELEASE]: BallPolynomialDataWire.BallPitchReleaseWire,
  [BallPolynomialType.PITCH_REFINED]: BallPolynomialDataWire.BallPitchRefinedWire,
  [BallPolynomialType.THROW_DATA]: BallPolynomialDataWire.BallThrowData,
};

const ballPacketFromWire = reverseRecord(ballPacketToWire);

interface Point {
  x: number;
  y: number;
  z: number;
}

function pointToWire(point?: Point): Vec3WireT | undefined {
  if (!point) return undefined;
  return new Vec3WireT(point.x, point.y, point.z);
}

function wireToPoint(vec?: Vec3WireT): Point | undefined {
  if (!vec) return undefined;
  return {
    x: vec.x,
    y: vec.y,
    z: vec.z,
  }
}

function vecToWire(vec?: [number, number, number]): Vec3WireT | undefined {
  if (!vec) return undefined;
  return new Vec3WireT(...vec);
}

function wireToVec(vec?: Vec3WireT): [number, number, number] | undefined {
  if (!vec) return undefined;
  return [vec.x, vec.y, vec.z];
}

interface BallPolynomialBase {
  timestamp: string;
  id: string;
}

export interface BounceData extends BallPolynomialBase {
  packet_type: "bounce_data"

  speed: number;
  end_speed: number;
  start_x: number;
  start_y: number;
  start_z: number;
  end_x: number;
  end_y: number;
  end_z: number;
  projected_x: number;
  projected_y: number;
  projected_z: number;
  polynomial_x_1: number;
  polynomial_x_2: number;
  polynomial_x_3: number;
  polynomial_y_1: number;
  polynomial_y_2: number;
  polynomial_y_3: number;
  polynomial_z_1: number;
  polynomial_z_2: number;
  polynomial_z_3: number;
  start_time: number;
  end_time: number;
  landing_time: number;
}

export interface HitData extends BallPolynomialBase {
  packet_type: "hit_data";

  speed: number;
  angle: number;
  direction: number;
  distance: number;
  actual_distance: number;
  projected_x: number;
  projected_y: number;
  projected_z: number;
  actual_x: number;
  actual_y: number;
  actual_z: number;
  polynomial_x_1: number;
  polynomial_x_2: number;
  polynomial_x_3: number;
  polynomial_x_4: number;
  polynomial_x_5: number;
  polynomial_x_6: number;
  polynomial_x_7: number;
  polynomial_x_8: number;
  polynomial_x_9: number;
  polynomial_y_1: number;
  polynomial_y_2: number;
  polynomial_y_3: number;
  polynomial_y_4: number;
  polynomial_y_5: number;
  polynomial_y_6: number;
  polynomial_y_7: number;
  polynomial_y_8: number;
  polynomial_y_9: number;
  polynomial_z_1: number;
  polynomial_z_2: number;
  polynomial_z_3: number;
  polynomial_z_4: number;
  polynomial_z_5: number;
  polynomial_z_6: number;
  polynomial_z_7: number;
  polynomial_z_8: number;
  polynomial_z_9: number;
  start_time: number;
  end_time: number;
  landing_time: number;
}

export interface HitLaunch extends BallPolynomialBase {
  packet_type: "hit_launch";

  speed: number;
  angle: number;
  direction: number;
}

export interface HitRefined extends BallPolynomialBase {
  packet_type: "hit_refined";

  launch_spin_rate: number;
  launch_spin_axis: number;
  apex_x: number;
  apex_y: number;
  apex_z: number;
}

export interface PitchData extends BallPolynomialBase {
  packet_type: "pitch_data";

  speed: number;
  type: string;
  sz_top: number;
  sz_bot: number;
  pitch_type: string;
  ReleaseData?: {
    angle: number;
    direction: number;
  };
  TrajectoryData?: {
    vertical_break: number;
    vertical_break_induced: number;
    horizontal_break: number;
    location?: Point;
    zone_speed: number;
    vertical_approach_angle: number;
    horizontal_approach_angle: number;
    zone_time: number;
    trajectory_polynomial_x?: [number, number, number];
    trajectory_polynomial_y?: [number, number, number];
    trajectory_polynomial_z?: [number, number, number];
  };
}

// NB: I don't think this actually exists in the data - can't find an example
//     in the frames I inspected
// export interface PitchLocation extends BallPolynomialBase {
//   packet_type: "pitch_location";

//   speed: number;
//   plate_x: number;
//   plate_y: number;
//   plate_z: number;
//   x0: number;
//   y0: number;
//   z0: number;
//   vx0: number;
//   vy0: number;
//   vz0: number;
//   ax: number;
//   ay: number;
//   az: number;
//   sz_top: number;
//   sz_bot: number;
//   sz_width: number;
//   sz_depth: number;
//   is_strike: boolean;
// }

export interface PitchRefined extends BallPolynomialBase {
  packet_type: "pitch_refined";

  sz_top: number;
  sz_bot: number;
  spin_rate: number;
  spin_axis: number;
  x: number;
  y: number;
  z: number;
  extension: number;
  perceived_speed: number;
}

// NB: I don't think this actually exists in the data - can't find an example
//     in the frames I inspected
// export interface PitchRelease extends BallPolynomialBase {
//   packet_type: "pitch_release";

//   speed: number;
// }

export interface ThrowData extends BallPolynomialBase {
  packet_type: "throw_data";

  speed: number;
  end_speed: number;
  start_x: number;
  start_y: number;
  start_z: number;
  end_x: number;
  end_y: number;
  end_z: number;
  projected_x: number;
  projected_y: number;
  projected_z: number;
  polynomial_x_1: number;
  polynomial_x_2: number;
  polynomial_x_3: number;
  polynomial_y_1: number;
  polynomial_y_2: number;
  polynomial_y_3: number;
  polynomial_z_1: number;
  polynomial_z_2: number;
  polynomial_z_3: number;
  start_time: number;
  end_time: number;
  landing_time: number;
}

export type BallPolynomial =
  BounceData |
  HitData |
  HitLaunch |
  HitRefined |
  PitchData |
  // TODO: do these exist???
  // PitchLocation |
  // PitchRelease |
  PitchRefined |
  ThrowData;

export class BallPolynomialSerializer {
  static serializeBinary(ballPacket: BallPolynomial, builder: Builder): number | undefined {
    const base = new BallPolynomialWireT(
      ballPacket.id,
      ballPacket.timestamp,
      ballPacketToWire[ballPacket.packet_type],
    );

    switch (ballPacket.packet_type) {
      case "bounce_data":
      {
        base.data = new BallBounceDataWireT(
          ballPacket.speed,
          ballPacket.end_speed,
          ballPacket.start_x,
          ballPacket.start_y,
          ballPacket.start_z,
          ballPacket.end_x,
          ballPacket.end_y,
          ballPacket.end_z,
          ballPacket.projected_x,
          ballPacket.projected_y,
          ballPacket.projected_z,
          ballPacket.polynomial_x_1,
          ballPacket.polynomial_x_2,
          ballPacket.polynomial_x_3,
          ballPacket.polynomial_y_1,
          ballPacket.polynomial_y_2,
          ballPacket.polynomial_y_3,
          ballPacket.polynomial_z_1,
          ballPacket.polynomial_z_2,
          ballPacket.polynomial_z_3,
          ballPacket.start_time,
          ballPacket.end_time,
          ballPacket.landing_time,
        );
        break;
      }

      case "hit_data":
      {
        base.data = new BallHitDataWireT(
          ballPacket.speed,
          ballPacket.angle,
          ballPacket.direction,
          ballPacket.distance,
          ballPacket.actual_distance,
          ballPacket.projected_x,
          ballPacket.projected_y,
          ballPacket.projected_z,
          ballPacket.actual_x,
          ballPacket.actual_y,
          ballPacket.actual_z,
          ballPacket.polynomial_x_1,
          ballPacket.polynomial_x_2,
          ballPacket.polynomial_x_3,
          ballPacket.polynomial_x_4,
          ballPacket.polynomial_x_5,
          ballPacket.polynomial_x_6,
          ballPacket.polynomial_x_7,
          ballPacket.polynomial_x_8,
          ballPacket.polynomial_x_9,
          ballPacket.polynomial_y_1,
          ballPacket.polynomial_y_2,
          ballPacket.polynomial_y_3,
          ballPacket.polynomial_y_4,
          ballPacket.polynomial_y_5,
          ballPacket.polynomial_y_6,
          ballPacket.polynomial_y_7,
          ballPacket.polynomial_y_8,
          ballPacket.polynomial_y_9,
          ballPacket.polynomial_z_1,
          ballPacket.polynomial_z_2,
          ballPacket.polynomial_z_3,
          ballPacket.polynomial_z_4,
          ballPacket.polynomial_z_5,
          ballPacket.polynomial_z_6,
          ballPacket.polynomial_z_7,
          ballPacket.polynomial_z_8,
          ballPacket.polynomial_z_9,
          ballPacket.start_time,
          ballPacket.end_time,
          ballPacket.landing_time,
        );
        break;
      }

      case "hit_launch":
      {
        base.data = new BallHitLaunchWireT(
          ballPacket.speed,
          ballPacket.angle,
          ballPacket.direction,
        )
        break;
      }

      case "hit_refined":
      {
        base.data = new BallHitRefinedWireT(
          ballPacket.launch_spin_rate,
          ballPacket.launch_spin_axis,
          ballPacket.apex_x,
          ballPacket.apex_y,
          ballPacket.apex_z,
        );
        break;
      }

      case "pitch_data":
      {
        base.data = new BallPitchDataWireT(
          ballPacket.speed,
          ballPacket.type,
          ballPacket.sz_top,
          ballPacket.sz_bot,
          ballPacket.pitch_type,
          ballPacket.ReleaseData
            ? new BallPitchDataReleaseDataWireT(
                ballPacket.ReleaseData.angle,
                ballPacket.ReleaseData.direction,
              )
            : undefined,
          ballPacket.TrajectoryData
            ? new BallPitchDataTrajectoryDataWireT(
              ballPacket.TrajectoryData.vertical_break,
              ballPacket.TrajectoryData.vertical_break_induced,
              ballPacket.TrajectoryData.horizontal_break,
              pointToWire(ballPacket.TrajectoryData.location),
              ballPacket.TrajectoryData.zone_speed,
              ballPacket.TrajectoryData.vertical_approach_angle,
              ballPacket.TrajectoryData.horizontal_approach_angle,
              ballPacket.TrajectoryData.zone_time,
              vecToWire(ballPacket.TrajectoryData.trajectory_polynomial_x),
              vecToWire(ballPacket.TrajectoryData.trajectory_polynomial_y),
              vecToWire(ballPacket.TrajectoryData.trajectory_polynomial_z),
            )
            : undefined
        );
        break;
      }



      case "pitch_refined":
      {
        base.data = new BallPitchRefinedWireT(
          ballPacket.sz_top,
          ballPacket.sz_bot,
          ballPacket.spin_rate,
          ballPacket.spin_axis,
          ballPacket.x,
          ballPacket.y,
          ballPacket.z,
          ballPacket.extension,
          ballPacket.perceived_speed,
        )
        break;
      }

      case "throw_data":
      {
        base.data = new BallThrowDataT(
          ballPacket.speed,
          ballPacket.end_speed,
          ballPacket.start_x,
          ballPacket.start_y,
          ballPacket.start_z,
          ballPacket.end_x,
          ballPacket.end_y,
          ballPacket.end_z,
          ballPacket.projected_x,
          ballPacket.projected_y,
          ballPacket.projected_z,
          ballPacket.polynomial_x_1,
          ballPacket.polynomial_x_2,
          ballPacket.polynomial_x_3,
          ballPacket.polynomial_y_1,
          ballPacket.polynomial_y_2,
          ballPacket.polynomial_y_3,
          ballPacket.polynomial_z_1,
          ballPacket.polynomial_z_2,
          ballPacket.polynomial_z_3,
          ballPacket.start_time,
          ballPacket.end_time,
          ballPacket.landing_time,
        )
        break;
      }

      default:
        // @ts-ignore
        console.error("unknown ball packet_type:", ballPacket.packet_type);
        break;
    }

    return base.pack(builder);
  }

  static deserializeBinary(ballWire?: BallPolynomialWire): BallPolynomial | undefined {
    if (!ballWire) return undefined;

    const unpacked = ballWire.unpack();

    if (!unpacked.data) {
      console.error("blank data ball packet");
      return undefined;
    }

    const base: BallPolynomialBase = {
      id: unpacked.id?.toString() || "unset",
      timestamp: unpacked.timestamp?.toString() || "missing",
    };

    switch (unpacked.dataType) {
      case BallPolynomialDataWire.BallBounceDataWire:
      {
        const data = unpacked.data as BallBounceDataWireT;

        return {
          packet_type: "bounce_data",
          ...base,

          speed: data.speed,
          end_speed: data.endSpeed,
          start_x: data.startX,
          start_y: data.startY,
          start_z: data.startZ,
          end_x: data.endX,
          end_y: data.endY,
          end_z: data.endZ,
          projected_x: data.projectedX,
          projected_y: data.projectedY,
          projected_z: data.projectedZ,
          polynomial_x_1: data.polynomialX1,
          polynomial_x_2: data.polynomialX2,
          polynomial_x_3: data.polynomialX3,
          polynomial_y_1: data.polynomialY1,
          polynomial_y_2: data.polynomialY2,
          polynomial_y_3: data.polynomialY3,
          polynomial_z_1: data.polynomialZ1,
          polynomial_z_2: data.polynomialZ2,
          polynomial_z_3: data.polynomialZ3,
          start_time: data.startTime,
          end_time: data.endTime,
          landing_time: data.landingTime,
        };
      }

      case BallPolynomialDataWire.BallHitDataWire:
      {
        const data = unpacked.data as BallHitDataWireT;

        return {
          packet_type: "hit_data",
          ...base,
          speed: data.speed,
          angle: data.angle,
          direction: data.direction,
          distance: data.distance,
          actual_distance: data.actualDistance,
          projected_x: data.projectedX,
          projected_y: data.projectedY,
          projected_z: data.projectedZ,
          actual_x: data.actualX,
          actual_y: data.actualY,
          actual_z: data.actualZ,
          polynomial_x_1: data.polynomialX1,
          polynomial_x_2: data.polynomialX2,
          polynomial_x_3: data.polynomialX3,
          polynomial_x_4: data.polynomialX4,
          polynomial_x_5: data.polynomialX5,
          polynomial_x_6: data.polynomialX6,
          polynomial_x_7: data.polynomialX7,
          polynomial_x_8: data.polynomialX8,
          polynomial_x_9: data.polynomialX9,
          polynomial_y_1: data.polynomialY1,
          polynomial_y_2: data.polynomialY2,
          polynomial_y_3: data.polynomialY3,
          polynomial_y_4: data.polynomialY4,
          polynomial_y_5: data.polynomialY5,
          polynomial_y_6: data.polynomialY6,
          polynomial_y_7: data.polynomialY7,
          polynomial_y_8: data.polynomialY8,
          polynomial_y_9: data.polynomialY9,
          polynomial_z_1: data.polynomialZ1,
          polynomial_z_2: data.polynomialZ2,
          polynomial_z_3: data.polynomialZ3,
          polynomial_z_4: data.polynomialZ4,
          polynomial_z_5: data.polynomialZ5,
          polynomial_z_6: data.polynomialZ6,
          polynomial_z_7: data.polynomialZ7,
          polynomial_z_8: data.polynomialZ8,
          polynomial_z_9: data.polynomialZ9,
          start_time: data.startTime,
          end_time: data.endTime,
          landing_time: data.landingTime,
        };
      }

      case BallPolynomialDataWire.BallHitLaunchWire:
      {
        const data = unpacked.data as BallHitLaunchWireT;

        return {
          packet_type: "hit_launch",
          ...base,
          speed: data.speed,
          angle: data.angle,
          direction: data.direction,
        };
      }

      case BallPolynomialDataWire.BallHitRefinedWire:
      {
        const data = unpacked.data as BallHitRefinedWireT;

        return {
          packet_type: "hit_refined",
          ...base,
          launch_spin_rate: data.launchSpinRate,
          launch_spin_axis: data.launchSpinAxis,
          apex_x: data.apexX,
          apex_y: data.apexY,
          apex_z: data.apexZ,
        };
      }

      case BallPolynomialDataWire.BallPitchDataWire:
      {
        const data = unpacked.data as BallPitchDataWireT;

        return {
          packet_type: "pitch_data",
          ...base,

          speed: data.speed,
          type: data.type?.toString() || "unset",
          sz_top: data.szTop,
          sz_bot: data.szBot,
          pitch_type: data.pitchType?.toString() || "unset",
          ReleaseData: data.releaseData
            ? {
              angle: data.releaseData.angle,
              direction: data.releaseData.direction,
            }
            : undefined,
          TrajectoryData: data.trajectoryData
          ? {
            vertical_break: data.trajectoryData.verticalBreak,
            vertical_break_induced: data.trajectoryData.verticalBreakInduced,
            horizontal_break: data.trajectoryData.horizontalBreak,
            location: wireToPoint(data.trajectoryData.location || undefined),
            zone_speed: data.trajectoryData.zoneSpeed,
            vertical_approach_angle: data.trajectoryData.verticalApproachAngle,
            horizontal_approach_angle: data.trajectoryData.horizontalApproachAngle,
            zone_time: data.trajectoryData.zoneTime,
            trajectory_polynomial_x: wireToVec(data.trajectoryData.trajectoryPolynomialX || undefined),
            trajectory_polynomial_y: wireToVec(data.trajectoryData.trajectoryPolynomialY || undefined),
            trajectory_polynomial_z: wireToVec(data.trajectoryData.trajectoryPolynomialZ || undefined),
            }
          : undefined,
        };
      }

      case BallPolynomialDataWire.BallPitchRefinedWire:
      {
        const data = unpacked.data as BallPitchRefinedWireT;

        return {
          packet_type: "pitch_refined",
          ...base,

          sz_top: data.szTop,
          sz_bot: data.szBot,
          spin_rate: data.spinRate,
          spin_axis: data.spinAxis,
          x: data.x,
          y: data.y,
          z: data.z,
          extension: data.extension,
          perceived_speed: data.perceivedSpeed,
        };
      }

      case BallPolynomialDataWire.BallThrowData:
      {
        const data = unpacked.data as BallThrowDataT;

        return {
          packet_type: "throw_data",
          ...base,

          speed: data.speed,
          end_speed: data.endSpeed,
          start_x: data.startX,
          start_y: data.startY,
          start_z: data.startZ,
          end_x: data.endX,
          end_y: data.endY,
          end_z: data.endZ,
          projected_x: data.projectedX,
          projected_y: data.projectedY,
          projected_z: data.projectedZ,
          polynomial_x_1: data.polynomialX1,
          polynomial_x_2: data.polynomialX2,
          polynomial_x_3: data.polynomialX3,
          polynomial_y_1: data.polynomialY1,
          polynomial_y_2: data.polynomialY2,
          polynomial_y_3: data.polynomialY3,
          polynomial_z_1: data.polynomialZ1,
          polynomial_z_2: data.polynomialZ2,
          polynomial_z_3: data.polynomialZ3,
          start_time: data.startTime,
          end_time: data.endTime,
          landing_time: data.landingTime,
        };
      }

      case BallPolynomialDataWire.NONE:
      default:
        console.error("null ball packet");
        return undefined;
    }
  }
}
